import {
	ChangeDetectionStrategy,
	Component,
	computed,
	EventEmitter,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { TuiInputNumber, TuiSkeleton } from '@taiga-ui/kit'
import { UtilsService } from '@shared/services/utils.service'
import { TuiError, TuiNumberFormat, TuiTextfield } from '@taiga-ui/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms'
import { TuiTextfieldControllerModule } from '@taiga-ui/legacy'
import { LabelType, NgxSliderModule, Options } from '@angular-slider/ngx-slider'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { MaxDecimalCount } from '@modules/stocks-module/general/constants/max-decimal-count'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { TextTransformerService } from '@shared/services/text-transformer.service'
import { InputNumberSignsDirective } from '@shared/directives/input-number-signs.directive'

@UseLocales({ ru, en })
@Component({
	selector: 'app-quantity-selector',
	templateUrl: './quantity-selector.component.html',
	styleUrl: './quantity-selector.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiError,
		TuiSkeleton,
		TuiTextfield,
		TuiInputNumber,
		ReactiveFormsModule,
		TuiTextfieldControllerModule,
		TuiNumberFormat,
		NgxSliderModule,
		TranslatePipe,
		InputNumberSignsDirective,
	],
})
export class QuantitySelectorComponent implements OnChanges, OnInit, OnDestroy {
	protected readonly map: MapConstructor = Map
	protected readonly math: Math = Math
	protected readonly minPercent: number = 0
	protected readonly maxPercent: number = 100
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly labelType: typeof LabelType = LabelType
	protected readonly maxDecimalCount: number = MaxDecimalCount

	public isLoadingOrders: InputSignal<boolean> = input<boolean>(false)
	public isCancelOpenedOrders: InputSignal<boolean> = input<boolean>(false)
	public position: InputSignal<PositionsInterface | null> = input<PositionsInterface | null>(null)

	public changeQty: OutputEmitterRef<number> = output<number>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _textTransformerService: TextTransformerService =
		inject<TextTransformerService>(TextTransformerService)

	private _activeControl: 'qty' | 'percent' | null = null
	private _isInitialOrdersLoad: boolean = true

	protected manualRefreshSlider: EventEmitter<void> = new EventEmitter<void>()

	protected form: FormGroup | null = null

	protected orders: WritableSignal<OrderInterface[]> = signal<OrderInterface[]>([])

	protected qtyAvailable: Signal<number> = computed<number>((): number => {
		const position: PositionsInterface | null = this.position()
		if (!position) return 0

		if (this.isCancelOpenedOrders()) return this.math.abs(position?.qty ?? 0)

		const filteredOrders: OrderInterface[] = this._ordersStocksHelperService.filterOrdersByPosition(
			position,
			this.orders(),
		)
		if (!filteredOrders.length) return +(position?.qty_available ?? 0)

		return this.math.abs(this._positionsStocksHelperService.calcQtyAvailable(position, filteredOrders))
	})

	protected sliderOptions: Signal<Options> = computed((): Options => {
		const qtyAvailable: number = this.qtyAvailable()
		const isLoadingOrders: boolean = this.isLoadingOrders()

		return {
			floor: 0,
			ceil: this.maxPercent,
			showTicks: true,
			showSelectionBar: true,
			step: 25,
			enforceStep: false,
			disabled: isLoadingOrders || !qtyAvailable,

			translate: (_: number, label: LabelType): string => {
				if (isLoadingOrders || !this.form) return '...'

				const qtyValue: number = this.form.get('qty')?.value
				const formQty: number = qtyValue != null && !isNaN(+qtyValue) ? this.math.abs(+qtyValue) : 0

				const labelMap: Map<LabelType, string> = new this.map<LabelType, string>([
					[this.labelType.Floor, this._textTransformerService.toQuantity(0)],
					[this.labelType.Ceil, this._textTransformerService.toQuantity(qtyAvailable)],
					[this.labelType.Low, this._textTransformerService.toQuantity(formQty)],
				])

				return this._translateService.instant('quantitySelector.qtySlider', { qty: labelMap.get(label) })
			},
		}
	})
	private _maxQtyValidator(control: AbstractControl): ValidationErrors | null {
		const value: number = control.value ?? 0
		const qtyAvailable: number = this.math.abs(+this.qtyAvailable().toFixed(this.maxDecimalCount))
		if (!qtyAvailable) return null

		return value > qtyAvailable ? { maxQty: true } : null
	}

	private _initForm(): void {
		this.form = this._formBuilder.group({
			qty: [null, [Validators.required, this._maxQtyValidator.bind(this)]],
			percent: [null, Validators.required],
		})
	}

	private _fillForm(): void {
		if (!this.form) return

		const qtyAvailable: number = this.math.abs(this.qtyAvailable())

		this.form.patchValue(
			{
				qty: qtyAvailable,
				percent: this.maxPercent,
			},
			{ emitEvent: false },
		)

		this.changeQty.emit(qtyAvailable)
		this.manualRefreshSlider.emit()
	}

	private _listenOrders(): void {
		this._ordersStocksHelperService.ordersResponseMap
			.get(this.orderStateEnum.OPEN)
			?.pipe(takeUntil(this._destroy$))
			.subscribe((response: OrdersResponseInterface | null): void => {
				const prevQtyAvailable: number = this.qtyAvailable()
				this.orders.set(response?.orders ?? [])
				if (this._isInitialOrdersLoad || this.qtyAvailable() !== prevQtyAvailable) {
					this._isInitialOrdersLoad = false
					this._fillForm()
				}
			})
	}

	private _listenFormControlsChanges(): void {
		if (!this.form) return

		this.form
			.get('qty')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((qty: number): void => {
				if (!this.form || this._activeControl === 'percent') return
				this._activeControl = 'qty'

				let percent: number = this.qtyAvailable()
					? (this.math.abs(qty) / this.math.abs(this.qtyAvailable())) * this.maxPercent
					: this.minPercent

				percent = +percent.toFixed(2)
				this.form.patchValue({ qty, percent }, { emitEvent: false })

				this.changeQty.emit(qty)
				this.manualRefreshSlider.emit()
				this._activeControl = null
			})

		this.form
			.get('percent')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((percent: number): void => {
				if (!this.form || this._activeControl === 'qty') return
				this._activeControl = 'percent'

				const qty: number = +((this.qtyAvailable() * percent) / this.maxPercent).toFixed(this.maxDecimalCount)

				this.form?.patchValue({ qty: this.math.abs(qty), percent }, { emitEvent: false })

				this.changeQty.emit(qty)
				this.manualRefreshSlider.emit()
				this._activeControl = null
			})
	}

	protected putCursorEnd(input: HTMLInputElement): void {
		this._utilsService.putCursorEnd(input)
	}

	protected getFieldError(fieldName: string): string | null {
		const field: AbstractControl | null | undefined = this.form?.get(fieldName)
		if (!field?.errors) return null

		if (field.errors['maxQty'])
			return this._translateService.instant('quantitySelector.maxQty', { max: this.qtyAvailable() })

		return null
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['isCancelOpenedOrders']) {
			this._fillForm()
		}
	}

	ngOnInit(): void {
		this._initForm()
		this._listenOrders()
		this._listenFormControlsChanges()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
